// src/pages/EventDetailPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ThemeContext } from '../context/ThemeContext';
import { eventService, commentService } from '../api';
import SocialShare from '../components/common/SocialShare';
import PageLoader from '../components/common/PageLoader';
import ErrorDisplay from '../components/common/ErrorDisplay';
import CommentSection from '../components/event/CommentSection';

// Styled components (conservés de votre code original)
const EventSection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Breadcrumbs = styled.div`
  margin-bottom: 2rem;
  color: ${props => props.theme.text};
  
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    margin: 0 0.5rem;
  }
`;

const EventContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const EventContent = styled.div``;

const EventImage = styled.div`
  width: 100%;
  height: 400px;
  background-image: url(${props => props.src || '/placeholder-image.jpg'});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const EventTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.text};
`;

const EventMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.text};
  opacity: 0.8;
  
  div {
    display: flex;
    align-items: center;
    
    i {
      margin-right: 0.5rem;
      color: ${props => props.theme.primary};
    }
  }
`;

const EventDescription = styled.div`
  margin-bottom: 2rem;
  color: ${props => props.theme.text};
  
  h2 {
    font-size: 1.5rem;
    margin: 1.5rem 0 1rem;
    color: ${props => props.theme.primary};
  }
  
  p {
    margin-bottom: 1rem;
    line-height: 1.7;
  }
  
  ul {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const EventSidebar = styled.div``;

const SidebarCard = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SidebarTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.primary};
`;

const EventDetails = styled.div`
  margin-bottom: 1.5rem;
  
  div {
    display: flex;
    margin-bottom: 0.8rem;
    
    i {
      width: 20px;
      margin-right: 0.8rem;
      color: ${props => props.theme.primary};
    }
    
    span {
      color: ${props => props.theme.text};
    }
  }
`;

const RegistrationForm = styled.form``;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.error};
  font-size: 0.8rem;
  margin-top: 0.3rem;
`;

const SuccessMessage = styled(motion.div)`
  background-color: ${props => props.theme.success};
  color: white;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
`;

const MapContainer = styled.div`
  height: 200px;
  margin-top: 1rem;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 4px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.8rem;
  background-color: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &:hover {
    background-color: ${props => props.theme.primaryDark || props.theme.primary + 'dd'};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  i {
    margin-right: 0.5rem;
  }
`;

const BackButton = styled(Link)`
  display: inline-block;
  padding: 0.8rem 1.5rem;
  background-color: ${props => props.theme.primary};
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.primaryDark || props.theme.primary + 'dd'};
    transform: translateY(-2px);
  }
  
  i {
    margin-right: 0.5rem;
  }
`;

const TeamMemberCard = styled.div`
  background-color: ${props => props.theme.backgroundSecondary || props.theme.background + '20'};
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  border: 1px solid ${props => props.theme.text}20;
`;

const TeamMemberHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.error};
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  
  i {
    margin-right: 0.3rem;
  }
`;

const AddMemberButton = styled.button`
  width: 100%;
  padding: 0.8rem;
  background-color: ${props => props.theme.primary}20;
  color: ${props => props.theme.primary};
  border: 1px dashed ${props => props.theme.primary};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1.5rem;
  
  &:hover {
    background-color: ${props => props.theme.primary}30;
  }
  
  i {
    margin-right: 0.5rem;
  }
`;

// Social engagement components
const SocialEngagementBar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
`;

const LikeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: ${props => props.active ? props.theme.primary : props.theme.text};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  padding: 0.5rem;
  border-radius: 4px;
  
  &:hover {
    background-color: ${props => props.theme.backgroundSecondary || props.theme.background + '20'};
  }
  
  i {
    color: ${props => props.active ? props.theme.primary : props.theme.text};
  }
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: ${props => props.theme.text};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  padding: 0.5rem;
  border-radius: 4px;
  
  &:hover {
    background-color: ${props => props.theme.backgroundSecondary || props.theme.background + '20'};
  }
`;

const CountBadge = styled.span`
  font-size: 0.9rem;
  color: ${props => props.theme.text};
`;

const EventTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const EventTag = styled.span`
  padding: 0.3rem 0.8rem;
  background-color: ${props => props.theme.primary}20;
  color: ${props => props.theme.primary};
  border-radius: 16px;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  
  i {
    margin-right: 0.4rem;
    font-size: 0.8rem;
  }
`;

const EventDetailPage = () => {
  const { eventSlug } = useParams();
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Chargement des données de l'événement
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        
        // Charger les détails de l'événement
        const response = await eventService.getById(eventSlug);
        console.log("Event data:", response.data);
        setEvent(response.data);
        
        // Vérifier si l'événement a déjà été liké par l'utilisateur actuel
        const storage_key = `event_like_${response.data.id || eventSlug}`;
        const hasLiked = localStorage.getItem(storage_key) !== null;
        setLiked(hasLiked);
        
        // Mettre à jour le nombre de participants
        const currentParticipants = response.data.participant_count || 0;
        setParticipantCount(currentParticipants);
        
        // Mettre à jour les likes
        setLikeCount(response.data.likes_count || 0);
        
        // Mettre à jour les commentaires
        setCommentCount(response.data.comments_count || 0);
        
        // Mise à jour du titre de la page
        document.title = `${response.data.title} | PropentaTech`;
      } catch (err) {
        console.error("Error fetching event:", err);
        setError(err.response?.data?.detail || t('errorLoadingEvent'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvent();
    
    return () => {
      document.title = 'PropentaTech';
    };
  }, [eventSlug, t]);
  
  // Chargement des commentaires
  useEffect(() => {
    const fetchComments = async () => {
      if (!event) return;
      
      try {
        setLoadingComments(true);
        const response = await commentService.getByEvent(event.id);
        setComments(response.data);
      } catch (err) {
        console.error("Error fetching comments:", err);
      } finally {
        setLoadingComments(false);
      }
    };
    
    fetchComments();
  }, [event]);
  
  // Fonction pour déterminer si c'est un événement en équipe
  const isTeamEvent = () => {
    // Par défaut, considérer comme un hackathon/événement d'équipe
    if (!event) return true;
    
    // Si le type est spécifiquement défini comme autre chose que hackathon/competition
    if (event.event_type && 
        !['hackathon', 'competition'].includes(event.event_type) &&
        !['atelier', 'workshop'].includes(event.event_type)) {
      return false;
    }
    
    return true;
  };
  
  // Initialiser les valeurs du formulaire
  const getInitialValues = () => {
    // Valeurs par défaut pour tous les cas
    const defaultValues = {
      name: '',
      email: '',
      phone: '',
      organization: '',
      expectations: '',
      // Pour les événements en équipe
      team_members: [{ name: '', email: '', phone: '' }],
      team_name: '',
      project_theme: '',
      project_description: '',
      video_url: ''
    };
    
    return defaultValues;
  };
  
  // Schéma de validation pour le formulaire
  const getValidationSchema = () => {
    if (isTeamEvent()) {
      return Yup.object({
        team_name: Yup.string().required(t('teamNameRequired')),
        project_theme: Yup.string().required(t('projectThemeRequired')),
        project_description: Yup.string()
          .required(t('projectDescriptionRequired'))
          .min(50, t('projectDescriptionMinLength')),
        video_url: Yup.string().url(t('invalidVideoUrl')).nullable(),
        team_members: Yup.array()
          .of(
            Yup.object({
              name: Yup.string().required(t('nameRequired')),
              email: Yup.string().email(t('invalidEmail')).required(t('emailRequired')),
              phone: Yup.string().required(t('phoneRequired'))
            })
          )
          .min(1, t('atLeastOneMemberRequired'))
          .max(event?.max_team_size || 4, t('maxTeamMembersExceeded'))
      });
    }
    
    return Yup.object({
      name: Yup.string().required(t('nameRequired')),
      email: Yup.string().email(t('invalidEmail')).required(t('emailRequired')),
      phone: Yup.string().required(t('phoneRequired')),
      organization: Yup.string(),
      expectations: Yup.string().required(t('expectationsRequired')).min(10, t('expectationsMinLength'))
    });
  };
  
  // Configuration du formulaire avec Formik
  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: getValidationSchema(),
    enableReinitialize: true,
    // Dans la fonction onSubmit du formik
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsSubmitting(true);
        setError(null);
        
        // Préparation des données d'inscription
        let registrationData;
        
        if (isTeamEvent()) {
          registrationData = {
            is_team: true,
            team_name: values.team_name,
            project_theme: values.project_theme,
            project_description: values.project_description,
            video_url: values.video_url || "",
            team_members: values.team_members,
            // Utiliser le premier membre comme contact principal
            name: values.team_members[0]?.name || "",
            email: values.team_members[0]?.email || "",
            phone: values.team_members[0]?.phone || ""
          };
        } else {
          registrationData = {
            is_team: false,
            name: values.name,
            email: values.email,
            phone: values.phone,
            organization: values.organization || "",
            expectations: values.expectations
          };
        }
        
        console.log("Sending registration data:", registrationData);
        
        // Utiliser le service d'inscription
        const response = await eventService.register(event.id, registrationData);
        console.log("Registration response:", response);
        
        // Mettre à jour le nombre de participants localement
        setParticipantCount(prevCount => prevCount + 1);
        setRegistrationSuccess(true);
        resetForm();
        
        setTimeout(() => {
          setRegistrationSuccess(false);
        }, 5000);
      } catch (err) {
        console.error("Error registering for event:", err);
        setError(err.response?.data?.detail || t('registrationFailedError'));
      } finally {
        setIsSubmitting(false);
      }
    }
  });
  
  // Calcul des places restantes
  const getRemainingSpots = () => {
    if (!event) return 0;
    return event.max_participants ? event.max_participants - participantCount : Infinity;
  };
  
  // Vérifier si l'événement est complet
  const isEventFull = () => {
    return getRemainingSpots() <= 0;
  };
  
  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const date = new Date(dateString);
    return date.toLocaleDateString(navigator.language, options);
  };
  
  // Gestion des likes
  const handleLikeToggle = async () => {
    try {
      const storage_key = `event_like_${event.id || event.slug}`;
      const uniqueId = localStorage.getItem(storage_key) || 
                     `anon-${Math.random().toString(36).substring(2, 10)}`;
      
      if (liked) {
        // Simulation locale du unlike (pour améliorer la réactivité)
        setLiked(false);
        setLikeCount(prev => Math.max(0, prev - 1));
        
        // Appel API pour unlike
        await eventService.unlike(event.id, uniqueId);
        localStorage.removeItem(storage_key);
      } else {
        // Simulation locale du like (pour améliorer la réactivité)
        setLiked(true);
        setLikeCount(prev => prev + 1);
        
        // Appel API pour like
        await eventService.like(event.id, uniqueId);
        localStorage.setItem(storage_key, uniqueId);
      }
    } catch (err) {
      console.error("Error toggling like:", err);
      // Restaurer l'état précédent en cas d'erreur
      setLiked(!liked);
      setLikeCount(prev => liked ? prev + 1 : Math.max(0, prev - 1));
    }
  };
  
  // Ajouter un commentaire
  const handleAddComment = async (commentText, authorName, authorEmail) => {
    if (!commentText.trim() || !authorName.trim()) return;
    
    try {
      const response = await commentService.create({
        event_id: event.id,
        text: commentText.trim(),
        author_name: authorName.trim(),
        author_email: authorEmail || null
      });
      
      // Ajouter le nouveau commentaire à la liste
      setComments(prev => [response.data, ...prev]);
      setCommentCount(prev => prev + 1);
    } catch (err) {
      console.error("Error adding comment:", err);
    }
  };
  
  // Afficher les tags de l'événement
  const renderEventTags = () => {
    if (!event || !event.tags || event.tags.length === 0) return null;
    
    return (
      <EventTagsContainer>
        {event.tags.map((tag, index) => (
          <EventTag key={index} theme={theme}>
            <i className="fas fa-tag"></i>
            {typeof tag === 'string' ? tag : tag.name}
          </EventTag>
        ))}
      </EventTagsContainer>
    );
  };
  
  // Afficher le type d'événement
  const getEventTypeLabel = () => {
    if (!event || !event.event_type) return t('event');
    
    const typeMapping = {
      'hackathon': t('hackathon'),
      'webinar': t('webinar'),
      'workshop': t('workshop'),
      'meetup': t('meetup'),
      'conference': t('conference'),
      'competition': t('competition')
    };
    
    return typeMapping[event.event_type] || t('event');
  };
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (error) {
    return <ErrorDisplay 
      error={error} 
      backLink="/events" 
      backLinkText={t('backToEvents')} 
    />;
  }
  
  if (!event) return null;
  
  return (
    <EventSection>
      <Container>
        <Breadcrumbs theme={theme}>
          <Link to="/">{t('home')}</Link> <span>/</span> 
          <Link to="/events">{t('events')}</Link> <span>/</span> 
          <span>{event.title}</span>
        </Breadcrumbs>
        
        <EventContainer>
          <EventContent>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {event.image && <EventImage src={event.image} />}
              
              <EventTitle theme={theme}>{event.title}</EventTitle>
              
              <EventMeta theme={theme}>
                <div>
                  <i className="far fa-calendar-alt"></i>
                  <span>
                    {formatDate(event.date || event.start_date)}
                    {(event.end_date && event.end_date !== (event.date || event.start_date)) && 
                      ` - ${formatDate(event.end_date)}`}
                  </span>
                </div>
                {(event.time || event.start_time) && (
                  <div>
                    <i className="far fa-clock"></i>
                    <span>
                      {event.time || event.start_time}
                      {(event.end_time) && ` - ${event.end_time}`}
                    </span>
                  </div>
                )}
                <div>
                  <i className="fas fa-map-marker-alt"></i>
                  <span>
                    {event.is_online ? t('onlineEvent') : (event.location || t('locationToBeDetermined'))}
                  </span>
                </div>
                <div>
                  <i className="fas fa-users"></i>
                  <span>
                    {participantCount} {t('registered')}
                  </span>
                </div>
                <div>
                  <i className="fas fa-tag"></i>
                  <span>{getEventTypeLabel()}</span>
                </div>
                {event.organizers && event.organizers.length > 0 && (
                  <div>
                    <i className="fas fa-user-tie"></i>
                    <span>{t('organizers')}: {event.organizers.map(o => 
                      typeof o === 'string' ? o : o.username || o.name
                    ).join(', ')}</span>
                  </div>
                )}
              </EventMeta>
              
              {renderEventTags()}
              
              <SocialEngagementBar>
                <LikeButton 
                  onClick={handleLikeToggle} 
                  active={liked} 
                  theme={theme}
                >
                  <i className={liked ? "fas fa-heart" : "far fa-heart"} style={{color: liked ? "#ff3366" : undefined}}></i>
                  <span>{liked ? t('liked') : t('like')}</span>
                  {likeCount > 0 && <CountBadge theme={theme}>({likeCount})</CountBadge>}
                </LikeButton>
                
                <ShareButton onClick={() => setShareModalOpen(true)} theme={theme}>
                  <i className="fas fa-share-alt"></i>
                  <span>{t('share')}</span>
                </ShareButton>
              </SocialEngagementBar>
              
              <EventDescription 
                theme={theme} 
                dangerouslySetInnerHTML={{ __html: event.description }} 
              />
              
              {/* Section des commentaires */}
              <CommentSection 
                comments={comments}
                loading={loadingComments}
                onAddComment={handleAddComment}
                theme={theme}
                t={t}
              />
            </motion.div>
          </EventContent>
          
          <EventSidebar>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <SidebarCard theme={theme}>
                <SidebarTitle theme={theme}>{t('eventDetails')}</SidebarTitle>
                
                <EventDetails theme={theme}>
                  <div>
                    <i className="far fa-calendar-alt"></i>
                    <span>
                      {formatDate(event.date || event.start_date)}
                      {(event.end_date && event.end_date !== (event.date || event.start_date)) && 
                        ` - ${formatDate(event.end_date)}`}
                    </span>
                  </div>
                  {(event.time || event.start_time) && (
                    <div>
                      <i className="far fa-clock"></i>
                      <span>
                        {event.time || event.start_time}
                        {(event.end_time) && ` - ${event.end_time}`}
                      </span>
                    </div>
                  )}
                  <div>
                    <i className="fas fa-map-marker-alt"></i>
                    <span>
                      {event.is_online ? t('onlineEvent') : (event.location || t('locationToBeDetermined'))}
                    </span>
                  </div>
                  {event.is_online && event.online_link && (
                    <div>
                      <i className="fas fa-video"></i>
                      <span>
                        <a href={event.online_link} target="_blank" rel="noopener noreferrer">
                          {t('joinOnline')}
                        </a>
                      </span>
                    </div>
                  )}
                  <div>
                    <i className="fas fa-users"></i>
                    <span>
                      {getRemainingSpots() === Infinity ? 
                        t('unlimitedSpots') : 
                        t('spotsRemaining', { count: getRemainingSpots() })}
                    </span>
                  </div>
                  {isTeamEvent() && (
                    <>
                      <div>
                        <i className="fas fa-user-friends"></i>
                        <span>
                          {t('teamSize')}: {event.min_team_size || 1} - {event.max_team_size || 4} {t('members')}
                        </span>
                      </div>
                    </>
                  )}
                  {event.status && (
                    <div>
                      <i className="fas fa-info-circle"></i>
                      <span>{t(`status_${event.status}`) || event.status}</span>
                    </div>
                  )}
                </EventDetails>
                
                {!event.is_online && event.location && event.latitude && event.longitude && (
                  <MapContainer>
                    <iframe 
                      src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d63692.99402649872!2d11.4606324!3d3.8504673!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x108bcf1e8e88686d%3A0xfca8362744a0eb3d!2sUniversit%C3%A9%20de%20Yaound%C3%A9%201-%20NGOA%20EKELLE!5e0!3m2!1sfr!2scm!4v1745766529979!5m2!1sfr!2scm"
                      title="Event Location"
                      allowFullScreen=""
                      loading="lazy"
                    ></iframe>
                  </MapContainer>
                )}
              </SidebarCard>
              
              <SidebarCard theme={theme}>
                <SidebarTitle theme={theme}>{t('registerForEvent')}</SidebarTitle>
                
                {registrationSuccess && (
                  <SuccessMessage
                    theme={theme}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
                    {t('registrationSuccess')}
                  </SuccessMessage>
                )}
                
                {event.status === 'completed' || event.status === 'cancelled' ? (
                  <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                    <p style={{ marginBottom: '1rem', color: theme.text }}>
                      {event.status === 'completed' ? 
                        t('eventAlreadyCompleted') : 
                        t('eventCancelled')}
                    </p>
                    <BackButton to="/events" theme={theme}>
                      <i className="fas fa-arrow-left"></i> {t('exploreOtherEvents')}
                    </BackButton>
                  </div>
                ) : isEventFull() ? (
                  <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                    <p style={{ marginBottom: '1rem', color: theme.text }}>{t('eventFullMessage')}</p>
                    <BackButton to="/events" theme={theme}>
                      <i className="fas fa-arrow-left"></i> {t('exploreOtherEvents')}
                    </BackButton>
                  </div>
                ) : (
                  <RegistrationForm onSubmit={formik.handleSubmit}>
                    {isTeamEvent() ? (
                      // Formulaire pour les événements en équipe (hackathon, compétition)
                      <>
                        <FormGroup>
                          <Label htmlFor="team_name" theme={theme}>{t('teamName')}</Label>
                          <Input
                            type="text"
                            id="team_name"
                            name="team_name"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.team_name}
                          />
                          {formik.touched.team_name && formik.errors.team_name && (
                            <ErrorMessage theme={theme}>{formik.errors.team_name}</ErrorMessage>
                          )}
                        </FormGroup>
                        
                        <FormGroup>
                          <Label htmlFor="project_theme" theme={theme}>{t('projectTheme')}</Label>
                          <Input
                            type="text"
                            id="project_theme"
                            name="project_theme"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.project_theme}
                          />
                          {formik.touched.project_theme && formik.errors.project_theme && (
                            <ErrorMessage theme={theme}>{formik.errors.project_theme}</ErrorMessage>
                          )}
                        </FormGroup>
                        
                        <FormGroup>
                          <Label htmlFor="project_description" theme={theme}>{t('projectDescription')}</Label>
                          <TextArea
                            id="project_description"
                            name="project_description"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.project_description}
                          />
                          {formik.touched.project_description && formik.errors.project_description && (
                            <ErrorMessage theme={theme}>{formik.errors.project_description}</ErrorMessage>
                          )}
                        </FormGroup>
                        
                        <FormGroup>
                          <Label htmlFor="video_url" theme={theme}>
                            {t('videoUrl')} 
                            {event.requires_video ? (
                              <span style={{ color: theme.error }}> ({t('required')})</span>
                            ) : (
                              <span> ({t('optional')})</span>
                            )}
                          </Label>
                          <Input
                            type="url"
                            id="video_url"
                            name="video_url"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.video_url}
                            placeholder="https://youtube.com/..."
                          />
                          {formik.touched.video_url && formik.errors.video_url && (
                            <ErrorMessage theme={theme}>{formik.errors.video_url}</ErrorMessage>
                          )}
                          <small style={{ color: theme.text, opacity: 0.8, display: 'block', marginTop: '0.3rem' }}>
                            {t('videoSizeLimit')}
                          </small>
                        </FormGroup>
                        
                        <SidebarTitle theme={theme}>{t('teamMembers')}</SidebarTitle>
                        
                        {formik.errors.team_members && typeof formik.errors.team_members === 'string' && (
                          <ErrorMessage theme={theme}>{formik.errors.team_members}</ErrorMessage>
                        )}
                        
                        {formik.values.team_members.map((member, index) => (
                          <TeamMemberCard key={index} theme={theme}>
                            <TeamMemberHeader>
                              <h4 style={{ margin: 0 }}>{t('member')} #{index + 1}</h4>
                              {index > 0 && (
                                <RemoveButton
                                  type="button"
                                  theme={theme}
                                  onClick={() => {
                                    const newMembers = [...formik.values.team_members];
                                    newMembers.splice(index, 1);
                                    formik.setFieldValue('team_members', newMembers);
                                  }}
                                >
                                  <i className="fas fa-times"></i> {t('remove')}
                                </RemoveButton>
                              )}
                            </TeamMemberHeader>
                            
                            <FormGroup>
                              <Label htmlFor={`team_members.${index}.name`} theme={theme}>{t('name')}</Label>
                              <Input
                                type="text"
                                id={`team_members.${index}.name`}
                                name={`team_members.${index}.name`}
                                theme={theme}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.team_members[index].name}
                              />
                              {formik.touched.team_members?.[index]?.name && 
                                formik.errors.team_members?.[index]?.name && (
                                <ErrorMessage theme={theme}>
                                  {formik.errors.team_members[index].name}
                                </ErrorMessage>
                              )}
                            </FormGroup>
                            
                            <FormGroup>
                              <Label htmlFor={`team_members.${index}.email`} theme={theme}>{t('email')}</Label>
                              <Input
                                type="email"
                                id={`team_members.${index}.email`}
                                name={`team_members.${index}.email`}
                                theme={theme}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.team_members[index].email}
                              />
                              {formik.touched.team_members?.[index]?.email && 
                                formik.errors.team_members?.[index]?.email && (
                                <ErrorMessage theme={theme}>
                                  {formik.errors.team_members[index].email}
                                </ErrorMessage>
                              )}
                            </FormGroup>
                            
                            <FormGroup>
                              <Label htmlFor={`team_members.${index}.phone`} theme={theme}>{t('phone')}</Label>
                              <Input
                                type="tel"
                                id={`team_members.${index}.phone`}
                                name={`team_members.${index}.phone`}
                                theme={theme}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.team_members[index].phone}
                              />
                              {formik.touched.team_members?.[index]?.phone && 
                                formik.errors.team_members?.[index]?.phone && (
                                <ErrorMessage theme={theme}>
                                  {formik.errors.team_members[index].phone}
                                </ErrorMessage>
                              )}
                            </FormGroup>
                          </TeamMemberCard>
                        ))}
                        
                        {formik.values.team_members.length < (event.max_team_size || 4) && (
                          <AddMemberButton
                            type="button"
                            theme={theme}
                            onClick={() => {
                              formik.setFieldValue('team_members', [
                                ...formik.values.team_members,
                                { name: '', email: '', phone: '' }
                              ]);
                            }}
                          >
                            <i className="fas fa-plus"></i> {t('addTeamMember')}
                          </AddMemberButton>
                        )}
                      </>
                    ) : (
                      // Formulaire standard pour événements individuels
                      <>
                        <FormGroup>
                          <Label htmlFor="name" theme={theme}>{t('name')}</Label>
                          <Input
                            type="text"
                            id="name"
                            name="name"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.name}
                          />
                          {formik.touched.name && formik.errors.name && (
                            <ErrorMessage theme={theme}>{formik.errors.name}</ErrorMessage>
                          )}
                        </FormGroup>
                        
                        <FormGroup>
                          <Label htmlFor="email" theme={theme}>{t('email')}</Label>
                          <Input
                            type="email"
                            id="email"
                            name="email"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                          />
                          {formik.touched.email && formik.errors.email && (
                            <ErrorMessage theme={theme}>{formik.errors.email}</ErrorMessage>
                          )}
                        </FormGroup>
                        
                        <FormGroup>
                          <Label htmlFor="phone" theme={theme}>{t('phone')}</Label>
                          <Input
                            type="tel"
                            id="phone"
                            name="phone"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.phone}
                          />
                          {formik.touched.phone && formik.errors.phone && (
                            <ErrorMessage theme={theme}>{formik.errors.phone}</ErrorMessage>
                          )}
                        </FormGroup>
                        
                        <FormGroup>
                          <Label htmlFor="organization" theme={theme}>{t('organization')} ({t('optional')})</Label>
                          <Input
                            type="text"
                            id="organization"
                            name="organization"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.organization}
                          />
                        </FormGroup>
                        
                        <FormGroup>
                          <Label htmlFor="expectations" theme={theme}>{t('expectations')}</Label>
                          <TextArea
                            id="expectations"
                            name="expectations"
                            theme={theme}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.expectations}
                          />
                          {formik.touched.expectations && formik.errors.expectations && (
                            <ErrorMessage theme={theme}>{formik.errors.expectations}</ErrorMessage>
                          )}
                        </FormGroup>
                      </>
                    )}
                    
                    <SubmitButton
                      type="submit"
                      theme={theme}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          {t('processing')}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-ticket-alt"></i>
                          {t('registerNow')}
                        </>
                      )}
                    </SubmitButton>
                  </RegistrationForm>
                )}
              </SidebarCard>
              
              <SidebarCard theme={theme}>
                <SidebarTitle theme={theme}>{t('shareEvent')}</SidebarTitle>
                <p style={{ marginBottom: '1rem', color: theme.text }}>{t('inviteFriends')}</p>
                <SocialShare 
                  title={event.title} 
                  hashtags={['PropentaTech', 'Event', event.title.replace(/\s+/g, '')]}
                />
              </SidebarCard>
            </motion.div>
          </EventSidebar>
        </EventContainer>
      </Container>
    </EventSection>
  );
};

export default EventDetailPage;